import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  addClickHandlersToElement,
  cleanPdfUrl,
  isProtectedPdfLink,
} from '@/shared/pdf-viewer/export';
import styles from '@/modules/articles/components/articles/article-view.module.scss';
import { useSubscription, messageService } from '@/core/messaging/export';
import { authService, useSessionId } from '@/core/auth/export';
import { analyticsService, AnalyticsEvent, AnalyticsTypes } from '@/shared/analytics/export';
import { Article } from '@/modules/articles/model/article';
import {
  MediaEmbedVariant,
  enhanceMediaEmbeds,
  stripMediaForMeasure,
} from '@/modules/articles/util/media-embeds';
import { useIsMediaMeasure } from '@/modules/articles/contexts/media-measure-context';

function addTableResponsiveDiv(table: HTMLTableElement) {
  const div = document.createElement('div');

  div.className = 'table-responsive';

  table.parentElement?.replaceChild(div, table);
  div.appendChild(table);

  let classes = table.getAttribute('class') || '';

  if (!classes.includes('table')) classes = `${classes} table`;

  table.setAttribute('class', classes);
}

export default function ArticleBodyDisplay({
  article,
  xhtml,
  pinnedWidget,
  variant = 'full',
}: {
  article: Article;
  xhtml: string;
  pinnedWidget?: boolean;
  variant?: MediaEmbedVariant;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [toggle, setToggle] = useState<boolean>(true);
  const isMeasure = useIsMediaMeasure();
  const html = useMemo(() => (isMeasure ? stripMediaForMeasure(xhtml) : xhtml), [isMeasure, xhtml]);

  const sessionId = useSessionId();
  const subject = authService.getSubjectData();

  useEffect(() => {
    const anchorListeners: { element: HTMLAnchorElement; handler: EventListener }[] = [];

    if (ref && xhtml) {
      if (!ref.current) return;

      addClickHandlersToElement(ref.current, () => {
        messageService.publish('article-model', {});
      });

      // custom scrolling behaviour on pinned articles (series as well)
      // add more when needed (probably different query selector)
      const widgetContainer = pinnedWidget
        ? (document.querySelector('.container-responsive-text') as HTMLElement | null)
        : null;
      const anchors = ref.current?.getElementsByTagName<'a'>('a');

      if (anchors) {
        for (const a of anchors) {
          const link = a.getAttribute('href');

          if (!link || !link.startsWith('#')) {
            a.setAttribute('target', '_blank');
          } else {
            if (widgetContainer) {
              const handler = (event: Event) => {
                event.preventDefault();
                event.stopPropagation();

                const targetId = link.substring(1);
                const targetElement = ref.current?.querySelector(
                  `[id="${targetId}"]`,
                ) as HTMLElement | null;

                if (targetElement && widgetContainer) {
                  widgetContainer.scrollTo({
                    top: targetElement.offsetTop - widgetContainer.offsetTop,
                    behavior: 'smooth',
                  });
                }
              };
              a.addEventListener('click', handler);
              anchorListeners.push({ element: a, handler });
            }
          }
        }
      }

      const tables = ref.current?.getElementsByTagName<'table'>('table');

      if (tables) {
        for (const a of tables) {
          try {
            addTableResponsiveDiv(a as HTMLTableElement);
          } catch (e) {
            console.error('Could not fix table', e);
          }
        }
      }

      if (anchors) {
        for (const a of anchors) {
          if (a.href && isProtectedPdfLink(a.href)) {
            const rawHrefs = [a.getAttribute('href') || '', a.href];
            const cleanHref = cleanPdfUrl(a.href);
            const label = a.innerText.trim();
            const isBareUrlLabel = rawHrefs.includes(label);

            a.setAttribute('href', cleanHref);
            a.className = ' btn btn-tertiary ' + styles.downloadPdfButton;
            a.textContent = isBareUrlLabel ? cleanHref : label;

            const handler = (event: Event) => {
              analyticsService.recordAnalyticsEvent(AnalyticsEvent.ARTICLE_PDF_VIEWED, {
                subject,
                object: {
                  type: AnalyticsTypes.ARTICLE,
                  id: article.uri,
                  title: article.headline,
                  genres: article.genre.map(g => ({ id: `${g.scheme}:${g.code}`, name: g.name })),
                  subjects: article.subject.map(s => ({
                    id: `${s.scheme}:${s.code}`,
                    name: s.name,
                  })),
                  authors: article.byline,
                },
                session: {
                  id: sessionId,
                },
              });
            };

            a.addEventListener('click', handler);

            anchorListeners.push({ element: a, handler });
          }
        }
      }
    }

    // Cleanup
    return () => {
      for (const { element, handler } of anchorListeners) {
        element.removeEventListener('click', handler);
      }
    };
    // eslint-disable-next-line
  }, [ref.current, xhtml, toggle]);

  // A layout effect so it runs in the same task that set innerHTML: feed GIF clips get
  // preload="none" before the browser starts fetching them.
  useLayoutEffect(() => {
    if (!ref.current || !xhtml || isMeasure) return;

    return enhanceMediaEmbeds(ref.current, variant);
    // eslint-disable-next-line
  }, [ref.current, xhtml, toggle, variant, isMeasure]);

  useSubscription('article-model', message => {
    setToggle(false);
  });

  useEffect(() => {
    setToggle(true);
  }, [toggle]);

  return (
    <>
      {' '}
      <div ref={ref} dangerouslySetInnerHTML={{ __html: html }} className={styles.article}></div>
      {toggle && <span />}
    </>
  );
}
